import { useState } from "react";
import type {
  EmbedPlanState,
  EntityList,
  Grain,
  MetricColumn,
  MetricKey,
  EmbedPlan,
  EmbedRank,
  ParentGrain,
} from "src/pages/ad-studio/insights/performance/types";
import {
  cubeLoad,
  useInsightsQuery,
} from "src/pages/ad-studio/insights/performance/use-insights-query";
import { useInsightsMeta } from "src/pages/ad-studio/insights/performance/use-insights-meta";
import { toEntityLabel } from "src/pages/ad-studio/insights/performance/utils/channels";
import {
  type EmbedAttrs,
  tablePlanSchema,
  toEmbedPlanState,
  toPlanResult,
  withRank,
} from "src/pages/ad-studio/insights/performance/utils/embeds";
import { entityListQuery } from "src/pages/ad-studio/insights/performance/utils/query";
import type {
  EntityKind,
  ListResult,
  Order,
  Page,
} from "src/pages/ad-studio/insights/performance/view/types";
import { usePaging } from "src/pages/ad-studio/insights/performance/view/use-paging";
import {
  columnSpecsOf,
  plannedColumns,
  sortedOrder,
  toListResult,
  toPage,
  toReportedMetrics,
} from "src/pages/ad-studio/insights/performance/view/utils";

export type TableEmbedAction =
  | { kind: "TableEmbedAction/sorted-column"; key: MetricKey }
  | { kind: "TableEmbedAction/paged"; page: number };

const PARENT_NOUNS: Record<ParentGrain, string> = {
  ad_set: "ad sets",
  campaign: "campaigns",
};

const nounOf = (list: EntityList): string => {
  switch (list.kind) {
    case "ads": {
      return "ads";
    }
    case "entities": {
      return PARENT_NOUNS[list.grain];
    }
    default: {
      return list satisfies never;
    }
  }
};

const grainOf = (list: EntityList): Grain => {
  switch (list.kind) {
    case "ads": {
      return "ad";
    }
    case "entities": {
      return list.grain;
    }
    default: {
      return list satisfies never;
    }
  }
};

export function useTableEmbedPlan(attrs: EmbedAttrs): {
  state: EmbedPlanState<EmbedPlan>;
} {
  const meta = useInsightsMeta();
  return {
    state: toEmbedPlanState({
      meta,
      toPlan: (loaded) =>
        toPlanResult(tablePlanSchema(loaded).safeParse(attrs)),
    }),
  };
}

export function useAdPerformanceTableEmbed({ plan }: { plan: EmbedPlan }): {
  state: ListResult<Page<EntityKind>>;
  performAction: (action: TableEmbedAction) => void;
} {
  const [sortOverride, setSortOverride] = useState<
    Order<MetricKey> | "plan-order"
  >("plan-order");
  const order = sortOverride === "plan-order" ? plan.query.order : sortOverride;
  const rank: EmbedRank =
    sortOverride === "plan-order" ? plan.rank : { kind: "by-query-order" };
  const queryAt = (page: number) => ({
    ...plan.query,
    order,
    metrics: plan.metrics.map((column) => column.key),
    page: { limit: plan.pageSize, offset: page * plan.pageSize },
  });
  const cubeAt = (page: number) =>
    withRank({
      cube: entityListQuery({ list: plan.list, query: queryAt(page) }),
      rank,
    });
  const paging = usePaging(JSON.stringify(cubeAt(0).query));
  const rows = useInsightsQuery(
    cubeLoad({
      cube: cubeAt(paging.state.page),
      wire: plan.list.view.wire,
    }),
  );
  const toSpecs = (shown: readonly MetricColumn[]) =>
    columnSpecsOf({
      label: toEntityLabel({
        grain: grainOf(plan.list),
        scope: plan.query.scope,
      }),
      meta: plan.meta,
      metrics: shown,
      order,
    });
  return {
    state: toListResult({
      result: rows,
      planned: plannedColumns({
        groupBy: plan.list,
        specs: toSpecs(plan.metrics),
      }),
      toPage: (loaded) =>
        toPage({
          list: loaded,
          specs: toSpecs(
            toReportedMetrics({ metrics: plan.metrics, rows: loaded.rows }),
          ),
          args: {
            paging: { page: paging.state.page, rowsPerPage: plan.pageSize },
            noun: nounOf(plan.list),
            emptyMessage: plan.emptyMessage,
          },
        }),
    }),
    performAction: (action) => {
      switch (action.kind) {
        case "TableEmbedAction/sorted-column": {
          return setSortOverride((previous) =>
            sortedOrder({
              order: previous === "plan-order" ? plan.query.order : previous,
              key: action.key,
            }),
          );
        }
        case "TableEmbedAction/paged": {
          return paging.performAction({
            kind: "PagingAction/paged",
            page: action.page,
          });
        }
        default: {
          return action satisfies never;
        }
      }
    },
  };
}
